import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import { useStoreApi } from "reactflow"

import CodeIcon from "@mui/icons-material/Code"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import { Avatar, Grid } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { InfoBlock } from "../../../../components/containers/InfoBlock"
import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { CalculatorGroupRetrieve } from "../../../../types/generated/api/CalculatorGroupRetrieve"
import type { PaginatedCalculatorListList } from "../../../../types/generated/api/PaginatedCalculatorListList"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { CalculatorDataItemType, CalculatorNodeProps, NodeType } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

import styles from "./Nodes.module.scss"

/**
 * CalculatorNode component that handles calculator selection and configuration in workflows.
 * Supports calculator data fetching, selection, and integration with workflow nodes.
 *
 * @param {object} data and object contains all passed data to this node
 * @param {boolean} menuView an indicator if the current view is menu or not
 * @param {string} id node id
 * @param {function} setAllNodes function that updates the workflow, must be passed down to all nodes
 * @return {React.ReactElement}
 */
export function CalculatorNode(props: Readonly<CalculatorNodeProps>): React.ReactElement {
  // Prop destructuring
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const calculators = data?.calculators
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const currentDataItemId = data?.calculator

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows")

  // React Flow hooks
  const store = useStoreApi()
  const { getNodes, setNodes } = store.getState()

  // State hooks
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false)
  const [selectedValue, setSelectedValue] = useState<CalculatorDataItemType | undefined | string>(
    currentDataItemId
      ? typeof currentDataItemId === "string"
        ? currentDataItemId
        : (currentDataItemId as CalculatorDataItemType)?.uuid
      : "",
  )

  // Data fetching queries
  // Fetch calculators
  const { isLoading: isCalculatorsLoading, data: calculatorsData } = useQuery<
    AxiosResponse<PaginatedCalculatorListList>,
    AxiosError
  >(
    ["calculators", projectId, 1, 200],
    () => KonanAPI.listCalculators({ projectId: projectId as string, page: 1, page_size: 200 }),
    {
      enabled: !!projectId && !menuView && !readMode && data?.nodeType === "CalculatorNode",
    },
  )

  const { isLoading: isCalculatorLoading } = useQuery<AxiosResponse<CalculatorGroupRetrieve>, AxiosError>(
    ["calculatorNode", selectedValue],
    () => KonanAPI.retrieveCalculator({ projectId: projectId as string, calculatorId: selectedValue as string }),
    {
      onSuccess: ({ data }) => {
        setNodes(
          getNodes().map((node) => {
            const newNode = { ...node }

            if (node.id === nodeId) {
              newNode.data = {
                ...newNode.data,
                calculator: {
                  created_at: data.active_version?.created_at,
                  created_by: data.active_version?.created_by,
                  equations: data.active_version?.equations,
                  uuid: data.uuid,
                  name: data.name,
                },
              }
            }

            return newNode
          }),
        )

        handleUpdateComputedFeature(
          data.active_version?.equations?.map((equation) => equation.computed_feature_name) ?? [],
        )
      },
      enabled: !!projectId && !menuView && typeof selectedValue === "string",
    },
  )

  // use unified removal hook
  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Node metadata (from centralized helpers)
  const metadata = nodeHelpers.getNodeMetadata("CalculatorNode")

  const handleUpdateComputedFeature = (newFeatures: Array<string>): void => {
    const newCfns = props?.data?.formik?.values?.computedFeatures?.filter(
      (feature: { uuid: string; value: string }) => feature.uuid !== nodeId,
    )

    props?.data?.formik?.setFieldValue("computedFeatures", [
      ...newCfns,
      ...newFeatures.map((feature) => ({ uuid: nodeId, value: feature })),
    ])
  }

  const onFoldClick = (): void => {
    props?.data?.formik?.setFieldValue("renderControl", props?.data?.formik?.values?.renderControl + 1)

    setIsDetailsOpen((prev) => {
      setNodes(
        getNodes().map((node) => {
          const newNode = { ...node }

          if (node.id === nodeId) {
            newNode.data = {
              ...newNode.data,
              isUnfolded: !prev,
            }
          }

          return newNode
        }),
      )

      return !prev
    })
  }

  return (
    <BaseWorkflowNode
      id={nodeId as string}
      menuView={menuView}
      readMode={readMode ?? undefined}
      nodeType={data?.nodeType as string}
      title={t("calculatorNode.nodeTitle")}
      iconProps={{
        element: <CodeIcon sx={{ color: theme.palette.base.text[1] }} />,
        className: metadata.iconClassName,
      }}
      handlesProps={{ showTarget: !menuView, showSource: !menuView }}
      width={metadata.width}
      onRemove={() => handleRemoveNode()}
    >
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        <Grid item xs={selectedValue ? 8 : 10}>
          {!menuView && !readMode ? (
            <SelectDataMenu
              nodeId={nodeId}
              dataType={data?.nodeType as NodeType}
              menuView={menuView}
              dataItemId={currentDataItemId}
              itemData={calculatorsData?.data ?? calculators}
              key={nodeId}
              selectedValue={selectedValue as string}
              setSelectedValue={(value) => {
                setIsDetailsOpen(false)
                setSelectedValue(value as string)
              }}
              isDataLoading={isCalculatorsLoading}
            />
          ) : (
            <Grid container justifyContent="space-between" alignItems="center">
              <ValueBlock
                value={
                  typeof currentDataItemId === "string"
                    ? currentDataItemId
                    : (currentDataItemId as CalculatorDataItemType)?.name ||
                      t("selectionNode.nodeBodies.noCalculatorSelected")
                }
                size={12}
                showTooltip={!menuView}
              />
            </Grid>
          )}
        </Grid>

        {/* Action buttons */}
        <Grid container item xs={selectedValue ? 4 : 2} columnSpacing={0.5} ml={0}>
          <Grid item xs={selectedValue ? 6 : 12}>
            <Button
              onClick={() => window.open(`/projects/${projectId}/calculators`, "_blank")}
              disabled={menuView}
              size={"regular"}
              fullWidth
              data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
              variant="secondary"
            >
              <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
            </Button>
          </Grid>
          {selectedValue && (
            <Grid item xs={6}>
              <Button
                onClick={onFoldClick}
                disabled={menuView || isCalculatorLoading}
                size={"regular"}
                fullWidth
                data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
                variant="secondary"
              >
                {isDetailsOpen ? (
                  <UnfoldLessIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
                ) : (
                  <UnfoldMoreIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
                )}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>

      {isDetailsOpen && (
        <Grid
          container
          item
          gap={1}
          mt={1}
          display={"flex"}
          flexDirection={"column"}
          className={styles.equationsContainer}
        >
          {currentDataItemId?.equations?.map((equation) => (
            <Grid
              item
              key={`${currentDataItemId.uuid}-${equation.order}`}
              alignItems={"center"}
              display={"flex"}
              gap={1}
            >
              <Avatar sx={{ width: 20, height: 20 }} className={styles.avatar}>
                <Typography variant="label-bold" color="base" variantColor={2}>
                  {equation.order}
                </Typography>
              </Avatar>

              <InfoBlock
                text={equation.computed_feature_name}
                type="default"
                textToolTip={equation.equation}
                textTransform="initial"
              />
            </Grid>
          ))}
        </Grid>
      )}
    </BaseWorkflowNode>
  )
}
